import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cn from "classnames";

import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";
import { WizardProfile } from "shared/components/WizardNew/components/WizardProfile";
import {
  WizardData,
  WizardProfileType,
} from "shared/components/WizardNew/types";

import { WEB_PUSH_TYPE } from "shared/constants/webPush";

import { simplyClick } from "shared/helpers/analytics";

import { useWizard, WizardStep } from "shared/hooks/useWizard";

import { getWizardProfiles } from "shared/services/wizard";

import styles from "@platformAssets/components/Wizard.module.scss";

type Props = { wizardData: WizardData };

const WizardProfiles = ({ wizardData }: Props) => {
  const { t } = useTranslation("wizardNew");
  const navigate = useNavigate();

  const [isLikedSomeProfile, setLikedSomeProfile] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const wizard = useWizard(wizardData);
  const step = wizard[WizardStep.Profiles];

  const [wizardProfiles, setWizardProfiles] = useState<WizardProfileType[]>([]);

  const handleLikedSomeProfile = (uid: number) => {
    setLikedSomeProfile(true);

    step.analytics?.like?.forEach((item) => {
      simplyClick(item, { destUserUid: uid }, false);
    });
  };

  useEffect(() => {
    getWizardProfiles()
      .then((data: any) => {
        setIsLoaded(true);
        setWizardProfiles(data.data.data.list);
      })
      .catch((e: Error) => {
        console.error(e);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, []);

  const handlePrevious = () => {
    navigate(`/${step?.previousStep}`);

    step.analytics?.back?.forEach((item) => {
      simplyClick(item, null, false);
    });
  };

  const handleSkip = () => {
    sessionStorage.setItem(WEB_PUSH_TYPE.INACTIVE, "true");
    window.location.href = "/app/";
    step.analytics?.skip?.forEach((item) => {
      simplyClick(item, null, false);
    });
  };

  const handleNext = () => {
    sessionStorage.setItem(WEB_PUSH_TYPE.INACTIVE, "true");
    window.location.href = "/app/";
    step.analytics?.next?.forEach((item) => {
      simplyClick(item, null, false);
    });
  };

  if (isLoaded && wizardProfiles.length < 4) {
    sessionStorage.setItem(WEB_PUSH_TYPE.INACTIVE, "true");
    window.location.href = "/app/";
  }
  return (
    <div className={cn(styles.wizardWrapper, styles.wizardProfilesWrapper)}>
      <div className={cn(styles.wizardContent)}>
        <div>
          <div className={styles.wizardTitle}>{step.title}</div>

          {!isLoaded && (
            <div className={styles.loaderWrap}>
              <SpinLoader
                size={LOADER_SIZE_LARGE}
                color={LOADER_COLOR_DARK}
                position={POSITION_ABSOLUTE}
              />
            </div>
          )}

          <div className={styles.wizardProfiles}>
            {wizardProfiles?.map((item) => (
              <WizardProfile
                key={item.uid}
                item={item}
                handleLikedSomeProfile={handleLikedSomeProfile}
              />
            ))}
          </div>
        </div>

        <div className={styles.wizardButtons}>
          <div className={styles.wizardButtonPrevious}>
            <button onClick={handlePrevious}>{t("buttonBack")}</button>
          </div>

          <a href="/app/" className={styles.wizardLogo} />

          <div
            className={cn(styles.wizardButtonNext, styles.wizardProfilesNext)}
          >
            <button
              className={!isLikedSomeProfile ? styles.disabled : ""}
              disabled={!isLikedSomeProfile}
              onClick={handleSkip}
            >
              {t("profiles.btn")}
            </button>
          </div>

          <div className={styles.wizardButtonSkip}>
            <button onClick={handleSkip}>{t("buttonSkip")}</button>
          </div>
        </div>

        <div className={styles.wizardProfilesNextMob}>
          <button
            className={!isLikedSomeProfile ? styles.disabled : ""}
            disabled={!isLikedSomeProfile}
            onClick={handleNext}
          >
            {t("profiles.btn")}
          </button>
        </div>
      </div>
    </div>
  );
};

export { WizardProfiles };
